import { useAction } from "convex/react";
import { useState } from "react";
import { RefreshCw } from "lucide-react";
import type { FunctionReturnType } from "convex/server";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@repo/ui";
import { api } from "@convex/_generated/api";

type Resultat = FunctionReturnType<typeof api.reports.rafraichirTemplates>;

/**
 * Resynchronise les templates Word des rapports depuis le Drive
 * (`reports.rafraichirTemplates`). Un template ajouté est récupéré tout seul au
 * premier rapport qui le demande ; un template modifié ou retiré du Drive ne
 * l'est qu'ici : la mise en ligne d'une modification reste un geste explicite.
 */
export function CarteTemplatesRapports() {
  const rafraichir = useAction(api.reports.rafraichirTemplates);
  const [enCours, setEnCours] = useState(false);
  const [resultat, setResultat] = useState<Resultat | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  const lancer = async () => {
    setEnCours(true);
    setErreur(null);
    setResultat(null);
    try {
      setResultat(await rafraichir({}));
    } catch (err) {
      setErreur(err instanceof Error ? err.message : "Erreur inconnue");
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Templates des rapports</CardTitle>
        <CardDescription>
          Les templates Word sont lus dans le dossier Google Drive des rapports. Un
          nouveau template est pris en compte automatiquement ; après une
          modification ou une suppression sur le Drive, mettez-les à jour ici.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Button onClick={() => void lancer()} disabled={enCours}>
          <RefreshCw className={`h-4 w-4 ${enCours ? "animate-spin" : ""}`} />
          {enCours ? "Mise à jour…" : "Mettre à jour depuis le Drive"}
        </Button>

        {erreur && <p className="text-sm text-destructive">{erreur}</p>}

        {resultat && (
          <div className="space-y-3 text-sm">
            <p className="font-medium">
              {resumer(resultat)}
            </p>

            {resultat.telecharges.length > 0 && (
              <ListeNoms titre="Mis à jour" noms={resultat.telecharges} variant="success" />
            )}

            {resultat.supprimes.length > 0 && (
              <ListeNoms titre="Retirés" noms={resultat.supprimes} variant="secondary" />
            )}

            {resultat.ignores.length > 0 && (
              <div className="space-y-1">
                <ListeNoms
                  titre="Ignorés"
                  noms={resultat.ignores}
                  variant="warning"
                />
                <p className="text-xs text-muted-foreground">
                  Seuls les fichiers .docx sont utilisés. Un Google Doc doit être
                  téléchargé au format .docx puis importé dans le dossier.
                </p>
              </div>
            )}

            <ListeNoms
              titre={`Disponibles (${resultat.templates.length})`}
              noms={resultat.templates}
              variant="outline"
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function resumer({ telecharges, supprimes }: Resultat): string {
  if (telecharges.length === 0 && supprimes.length === 0) {
    return "Aucun changement : tous les templates étaient à jour.";
  }
  const pluriel = (n: number) => (n > 1 ? "s" : "");
  return [
    telecharges.length > 0 &&
      `${telecharges.length} template${pluriel(telecharges.length)} mis à jour`,
    supprimes.length > 0 &&
      `${supprimes.length} template${pluriel(supprimes.length)} retiré${pluriel(supprimes.length)}`,
  ]
    .filter(Boolean)
    .join(", ")
    .concat(".");
}

function ListeNoms({
  titre,
  noms,
  variant,
}: {
  titre: string;
  noms: string[];
  variant: "success" | "warning" | "secondary" | "outline";
}) {
  return (
    <div className="space-y-1.5">
      <p className="text-muted-foreground">{titre}</p>
      <div className="flex flex-wrap gap-1.5">
        {noms.map((nom) => (
          <Badge key={nom} variant={variant} className="font-mono font-normal">
            {nom}
          </Badge>
        ))}
      </div>
    </div>
  );
}
